import { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { BlogComment, BlogReactionKind } from '../blogSchemas';
import { canDeleteBlogComment, getCurrentUserIdFromStorage } from '../blogPermissions';
import { formatDateTime } from '../blogUtils';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';
import ReactionBar from '../../calendar/components/ReactionBar';
import { ReactionCounts } from '../../Interfaces/IEventInteraction';
import { primaryButtonSx } from '../../core/pvtaStyles';

const wasEdited = (createdAt: string, updatedAt?: string | null): boolean => {
    if (!updatedAt) {
        return false;
    }
    const created = new Date(createdAt).getTime();
    const updated = new Date(updatedAt).getTime();
    if (Number.isNaN(created) || Number.isNaN(updated)) {
        return false;
    }
    return updated - created > 1000;
};

interface BlogCommentItemProps {
    comment: BlogComment;
    isWorking: boolean;
    onCreateReply: (parentId: number, text: string) => Promise<void>;
    onDeleteComment: (commentId: number) => Promise<void>;
    onToggleReaction: (commentId: number, emoji: BlogReactionKind) => Promise<void>;
}

const BlogCommentItem: React.FC<BlogCommentItemProps> = ({
    comment,
    isWorking,
    onCreateReply,
    onDeleteComment,
    onToggleReaction,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [replyOpen, setReplyOpen] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [avatarLoadError, setAvatarLoadError] = useState(false);

    const currentUserId = useMemo(() => getCurrentUserIdFromStorage(), []);
    const canDelete = canDeleteBlogComment(comment.author.id);
    const initials = useMemo(() => comment.author.name.slice(0, 2).toUpperCase(), [comment.author.name]);
    const isEdited = useMemo(() => wasEdited(comment.createdAt, comment.updatedAt), [comment.createdAt, comment.updatedAt]);

    const avatarUrl = useMemo(() => {
        const value = comment.author.avatarUrl;
        if (!value) return null;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }, [comment.author.avatarUrl]);

    const { src: authenticatedAvatarSrc } = useAuthenticatedImage(avatarUrl);
    const avatarSrc = avatarLoadError || !authenticatedAvatarSrc ? undefined : authenticatedAvatarSrc;

    useEffect(() => {
        setAvatarLoadError(false);
    }, [avatarUrl]);

    const handleReplySubmit = async (): Promise<void> => {
        const trimmed = replyText.trim();
        if (!trimmed) return;
        await onCreateReply(comment.id, trimmed);
        setReplyText('');
        setReplyOpen(false);
    };

    return (
        <Box sx={{ pl: comment.parentId ? { xs: 1, sm: 3 } : 0, pt: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ pb: { xs: '8px !important', sm: '12px !important' }, px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 1.5 } }}>
                    <Stack direction="row" spacing={{ xs: 1, sm: 1.25 }} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <Avatar
                            src={avatarSrc}
                            imgProps={{ onError: () => setAvatarLoadError(true) }}
                            sx={{ width: { xs: 30, sm: 34 }, height: { xs: 30, sm: 34 }, flexShrink: 0 }}
                        >
                            {!avatarSrc && initials}
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                        {comment.author.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDateTime(comment.createdAt)}
                                    </Typography>
                                    {isEdited && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            bearbeitet
                                        </Typography>
                                    )}
                                </Stack>

                                {canDelete && (
                                    <>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                                            disabled={isWorking}
                                            sx={{ minHeight: 36, minWidth: 36, flexShrink: 0 }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
                                            <MenuItem
                                                onClick={() => { setMenuAnchorEl(null); void onDeleteComment(comment.id); }}
                                                disabled={isWorking}
                                            >
                                                Löschen
                                            </MenuItem>
                                        </Menu>
                                    </>
                                )}
                            </Box>

                            <Typography
                                variant="body2"
                                sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                            >
                                {comment.text}
                            </Typography>

                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={0.75}
                                flexWrap="nowrap"
                                sx={{ mt: 0.5, minWidth: 0 }}
                            >
                                <Button
                                    size="small"
                                    startIcon={<ReplyOutlinedIcon fontSize="small" />}
                                    onClick={() => setReplyOpen((v) => !v)}
                                    variant="text"
                                    sx={{
                                        minHeight: isMobile ? 28 : 34,
                                        minWidth: 0,
                                        px: 0.5,
                                        py: 0,
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                    }}
                                >
                                    Antworten
                                </Button>
                                <Box sx={{ minWidth: 0, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                                    <ReactionBar
                                        counts={comment.counts as ReactionCounts}
                                        userNamesByReaction={comment.reactionUserNames}
                                        viewerEmoji={comment.viewerEmoji}
                                        disabled={isWorking}
                                        onToggle={(kind) => void onToggleReaction(comment.id, kind as BlogReactionKind)}
                                    />
                                </Box>
                            </Stack>

                            {replyOpen && (
                                <Box sx={{ mt: 1.25 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Antwort schreiben"
                                        value={replyText}
                                        onChange={({ target }) => setReplyText(target.value)}
                                    />
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => { setReplyOpen(false); setReplyText(''); }}
                                            disabled={isWorking}
                                            sx={{ minWidth: 0, px: 1.5 }}
                                        >
                                            Abbrechen
                                        </Button>
                                        <Button
                                            size="small"
                                            disabled={isWorking || !replyText.trim()}
                                            onClick={() => void handleReplySubmit()}
                                            sx={{ ...primaryButtonSx, minWidth: 0, px: 1.5 }}
                                        >
                                            Senden
                                        </Button>
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {comment.replies.map((reply) => (
                <BlogCommentItem
                    key={reply.id}
                    comment={reply}
                    isWorking={isWorking}
                    onCreateReply={onCreateReply}
                    onDeleteComment={onDeleteComment}
                    onToggleReaction={onToggleReaction}
                />
            ))}
        </Box>
    );
};

export default BlogCommentItem;
